import { useEffect, useState } from 'react';
import ConnectClaudeInstructions from '../components/ConnectClaudeInstructions';
import { Avatar } from '../components/AppShell';
import { useWorkspaceContext } from '../contexts/WorkspaceContext';
import {
  getResolvedTheme,
  getThemePreference,
  setThemePreference,
  subscribeToThemePreference,
  type ThemePreference,
} from '../theme';

export default function UserSettings() {
  const { user } = useWorkspaceContext();
  const [themePreference, setThemePreferenceState] = useState<ThemePreference>(() => getThemePreference());

  useEffect(() => {
    return subscribeToThemePreference((preference) => {
      setThemePreferenceState(preference);
    });
  }, []);

  const resolvedTheme = getResolvedTheme(themePreference);

  return (
    <div className="px-[var(--sp-6)] pt-[16px] pb-[var(--sp-8)]">
      <h1
        className="text-[24px] font-bold mb-[var(--sp-2)]"
        style={{ color: 'var(--color-text-neutral-strong)' }}
      >
        Personal Settings
      </h1>
      <p
        className="text-[14px] mb-[var(--sp-8)]"
        style={{ color: 'var(--color-text-neutral-subtle)' }}
      >
        Manage your profile, theme preference, and personal Claude setup.
      </p>

      <section className="mb-[var(--sp-8)]">
        <h2
          className="text-[16px] font-semibold mb-[var(--sp-4)]"
          style={{ color: 'var(--color-text-neutral-strong)' }}
        >
          Profile
        </h2>
        <div
          className="rounded-[var(--radius-md)] p-[var(--sp-5)] border flex items-center gap-[var(--sp-4)]"
          style={{
            background: 'var(--color-surface-neutral-default)',
            borderColor: 'var(--color-border-neutral-subtle-default)',
          }}
        >
          <Avatar name={user.name} avatarUrl={user.avatar_url} size={48} />
          <div>
            <div
              className="text-[15px] font-semibold"
              style={{ color: 'var(--color-text-neutral-strong)' }}
            >
              {user.name}
            </div>
            <div
              className="text-[13px]"
              style={{ color: 'var(--color-text-neutral-subtle)' }}
            >
              {user.email}
            </div>
          </div>
        </div>
      </section>

      <section className="mb-[var(--sp-8)]">
        <h2
          className="text-[16px] font-semibold mb-[var(--sp-4)]"
          style={{ color: 'var(--color-text-neutral-strong)' }}
        >
          Appearance
        </h2>
        <div
          className="rounded-[var(--radius-md)] p-[var(--sp-5)] border"
          style={{
            background: 'var(--color-surface-neutral-default)',
            borderColor: 'var(--color-border-neutral-subtle-default)',
          }}
        >
          <label className="flex flex-col gap-[var(--sp-2)] max-w-[280px]">
            <span
              className="text-[13px] font-medium"
              style={{ color: 'var(--color-text-action-medium-inactive)' }}
            >
              Theme
            </span>
            <select
              value={themePreference}
              onChange={(e) => setThemePreference(e.target.value as ThemePreference)}
              className="h-[40px] rounded-[var(--radius-md)] border px-[var(--sp-3)] text-[13px] font-medium"
              style={{
                background: 'var(--color-surface-neutral-default)',
                borderColor: 'var(--color-border-neutral-subtle-default)',
                color: 'var(--color-text-neutral-strong)',
              }}
            >
              <option value="system">
                System ({resolvedTheme === 'dark' ? 'Dark' : 'Light'})
              </option>
              <option value="light">Light</option>
              <option value="dark">Dark</option>
            </select>
          </label>
        </div>
      </section>

      <ConnectClaudeInstructions
        intro="Add Exhibit to your own Claude clients so you can publish artifacts directly from your conversations."
        claudeCodeDescription="Run this command in your terminal."
        claudeDesktopDescription="Go to Settings > Connectors > Add Connector. Paste this URL and authenticate with Google."
        footer="Use Streamable HTTP transport with the URL above. OAuth 2.1 with PKCE is required. The server supports dynamic client registration."
      />
    </div>
  );
}
